import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Users, Plus, Search, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  active: "#10B981", test: "#00D4FF", pause: "#F59E0B", problem: "#EF4444",
};

export default function ModelsCRM() {
  const [models, setModels] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    try {
      const [m, a] = await Promise.all([api.crmListModels(), api.igListAccounts()]);
      setModels(m); setAccounts(a);
    } catch { toast.error("Erreur chargement"); }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = models.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) ||
    (m.instagram_handle || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.niche || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="models-crm-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// CRM MODÈLES</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Roster</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">Toutes tes créatrices au même endroit. Fiche complète, comptes liés, stats, tâches, objectifs.</p>
        </div>
        <button data-testid="add-model-btn" onClick={() => setShowAdd(true)}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          <Plus className="w-4 h-4" /> Nouvelle modèle
        </button>
      </header>

      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
          <input data-testid="model-search" placeholder="Rechercher (nom, handle, niche)..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm pl-10 pr-3 py-2 text-sm text-white placeholder-zinc-700 focus:border-[#CCFF00]/40 outline-none" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="md:col-span-3 border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
            <div className="text-zinc-500 text-sm mb-4">{models.length === 0 ? "Aucune modèle dans ton roster." : "Aucun résultat."}</div>
            {models.length === 0 && (
              <button onClick={() => setShowAdd(true)} className="text-[#CCFF00] text-sm font-mono uppercase tracking-wider hover:underline">
                + Ajouter ta première modèle
              </button>
            )}
          </div>
        ) : filtered.map((m, i) => {
          const linkedAccounts = accounts.filter(a => a.model_id === m.id);
          const totalFollowers = linkedAccounts.reduce((s, a) => s + (a.followers || 0), 0);
          return (
            <Link key={m.id} to={`/models/${m.id}`} data-testid={`model-card-${i}`}
              className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 hover:border-[#CCFF00]/30 transition group">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {m.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-white truncate group-hover:text-[#CCFF00] transition">{m.name}</div>
                  {m.instagram_handle && <div className="text-[11px] font-mono text-zinc-500">@{m.instagram_handle}</div>}
                </div>
                <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm shrink-0"
                  style={{ backgroundColor: (STATUS_COLORS[m.status] || "#666") + "20", color: STATUS_COLORS[m.status] || "#666" }}>
                  {m.status}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3">
                <Mini label="Comptes" v={linkedAccounts.length} />
                <Mini label="Abonnés" v={totalFollowers > 1000 ? `${Math.round(totalFollowers / 100) / 10}k` : totalFollowers} />
                <Mini label="Plateforme" v={m.main_platform} />
              </div>

              <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                {m.niche && <span className="px-1.5 py-0.5 bg-white/5 rounded-sm">{m.niche}</span>}
                {m.target_country && <span className="px-1.5 py-0.5 bg-white/5 rounded-sm">{m.target_country}</span>}
              </div>
            </Link>
          );
        })}
      </div>

      {showAdd && <AddModelModal onClose={() => setShowAdd(false)} onSuccess={refresh} />}
    </div>
  );
}

const Mini = ({ label, v }) => (
  <div className="border border-[#1E1E24] bg-[#070708] rounded-sm p-2 text-center">
    <div className="font-mono text-sm text-white">{v}</div>
    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
  </div>
);

function AddModelModal({ onClose, onSuccess }) {
  const [f, setF] = useState({ name: "", instagram_handle: "", main_platform: "OF", niche: "", language: "fr",
                                status: "active", target_country: "", tracking_link: "", objectives: "", private_notes: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.crmCreateModel(f); toast.success("Modèle créée"); onSuccess(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto" data-testid="add-model-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-xl text-white">Nouvelle modèle</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <Field label="Nom" testid="m-name" required value={f.name} onChange={v => setF({...f, name: v})} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Handle Instagram" testid="m-handle" value={f.instagram_handle} onChange={v => setF({...f, instagram_handle: v})} placeholder="lina_official" />
          <Sel label="Plateforme principale" testid="m-platform" value={f.main_platform} onChange={v => setF({...f, main_platform: v})} opts={["OF", "Fansly", "MYM", "Other"]} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Niche" testid="m-niche" value={f.niche} onChange={v => setF({...f, niche: v})} />
          <Field label="Pays" testid="m-country" value={f.target_country} onChange={v => setF({...f, target_country: v})} />
          <Sel label="Langue" testid="m-lang" value={f.language} onChange={v => setF({...f, language: v})} opts={["fr", "en", "es", "de", "it"]} />
        </div>
        <Sel label="Statut" testid="m-status" value={f.status} onChange={v => setF({...f, status: v})} opts={["active", "test", "pause", "problem"]} />
        <Field label="Lien tracking" testid="m-tracking" value={f.tracking_link} onChange={v => setF({...f, tracking_link: v})} placeholder="https://..." />
        <Area label="Objectifs" testid="m-objectives" value={f.objectives} onChange={v => setF({...f, objectives: v})} />
        <Area label="Notes privées" testid="m-notes" value={f.private_notes} onChange={v => setF({...f, private_notes: v})} />
        <button type="submit" disabled={loading} data-testid="m-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "Création..." : "Créer la modèle"}
        </button>
      </form>
    </div>
  );
}

const Field = ({ label, testid, value, onChange, required, placeholder }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <input data-testid={testid} required={required} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
  </div>
);
const Sel = ({ label, testid, value, onChange, opts }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
      {opts.map(o => <option key={o}>{o}</option>)}
    </select>
  </div>
);
const Area = ({ label, testid, value, onChange }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <textarea data-testid={testid} rows={2} value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
  </div>
);
